import { useTranslation } from 'react-i18next';
import styles from './OnboardingProgressBar.module.css';

interface OnboardingProgressBarProps {
  /** Step-counter mode (back-compat). Ignored when progressPct/indeterminate is set. */
  step?: number;
  etaSeconds?: number;
  totalSteps?: number;
  /** Percentage mode (0–100): drives the fill directly and hides the "Step N/total" label. */
  progressPct?: number;
  /** Animated fill for unknown-duration overshoot. Implies percentage mode. */
  indeterminate?: boolean;
  /** Overrides the right-hand label text (e.g. "~12s left" / "Almost ready…"). */
  label?: string;
}

export function OnboardingProgressBar({
  step,
  etaSeconds,
  totalSteps = 6,
  progressPct,
  indeterminate = false,
  label,
}: OnboardingProgressBarProps): JSX.Element {
  const { t } = useTranslation('flows');
  const usesPct = progressPct != null || indeterminate;

  const clampedStep = Math.max(0, Math.min(step ?? 0, totalSteps));
  const widthPct = usesPct
    ? Math.max(0, Math.min(progressPct ?? 0, 100))
    : (clampedStep / totalSteps) * 100;

  const rightLabel = label ?? t('onboarding.progress.etaSeconds', { seconds: etaSeconds ?? 0 });

  return (
    <div
      className={styles.wrap}
      role="progressbar"
      aria-valuenow={usesPct ? Math.round(widthPct) : clampedStep}
      aria-valuemin={0}
      aria-valuemax={usesPct ? 100 : totalSteps}
      aria-label={rightLabel}
    >
      <div className={styles.track}>
        <div
          className={`${styles.fill} ${indeterminate ? styles.indeterminate : ''}`}
          data-indeterminate={indeterminate ? 'true' : undefined}
          style={indeterminate ? undefined : { width: `${widthPct}%` }}
        />
      </div>
      <div className={styles.labels}>
        {!usesPct && (
          <span className={styles.stepLabel}>
            {t('onboarding.progress.stepN', { current: clampedStep, total: totalSteps })}
          </span>
        )}
        <span className={styles.etaLabel}>{rightLabel}</span>
      </div>
    </div>
  );
}
